// autopilot-unlock-command.ts
//
// Phase 05 (AC-02): `avatar autopilot unlock [--mission <id>] [--json]` —
// gỡ lock mồ côi sau crash/SIGKILL/reboot (KHÔNG gỡ khi supervisor còn
// sống). Mẫu bố cục theo autopilot-doctor-command.ts.
import { join } from "node:path";
import type { Command } from "commander";
import { resolveMission } from "../lib/autopilot/mission-resolver.js";
import { releaseOrphanedSupervisorLocks } from "../lib/autopilot/orphaned-supervisor-lock-release.js";
import { realLivenessProbe } from "../lib/autopilot/process-liveness-probe.js";
// Req 9 (phase 05, NEW-3): lets the orphan reconciler's supervisor_crash
// evidence name an unlock that already killed the worker.
import { writeUnlockKilledWorkerMarker } from "../lib/autopilot/unlock-killed-worker-marker.js";
import { writeMachineReadableJson } from "../lib/machine-readable-json-output-contract.js";
import { spawnAsyncCapture } from "../lib/spawn-async-capture-with-timeout.js";
import { log } from "../lib/terminal-logger.js";

export interface RegisterAutopilotUnlockCommandDeps {
  resolveWorkspaceRoot: (repoFlag: string | undefined) => Promise<string>;
  machineLockPath: string;
}

async function defaultProcessGroupId(pid: number): Promise<number | undefined> {
  try {
    const r = await spawnAsyncCapture("ps", ["-o", "pgid=", "-p", String(pid)], {
      timeoutMs: 5_000,
      forwardSigint: false,
    });
    const pgid = Number.parseInt(r.stdout.trim(), 10);
    return Number.isInteger(pgid) && pgid > 0 ? pgid : undefined;
  } catch {
    return undefined;
  }
}

async function pollUntilGone(pgid: number, deadlineMs: number): Promise<void> {
  const deadline = Date.now() + deadlineMs;
  while (Date.now() < deadline) {
    try {
      process.kill(-pgid, 0);
    } catch {
      return;
    }
    await new Promise((r) => setTimeout(r, 50));
  }
}

async function defaultKillGroup(pgid: number): Promise<void> {
  try {
    process.kill(-pgid, "SIGTERM");
  } catch {
    return;
  }
  await pollUntilGone(pgid, 5_000);
  try {
    process.kill(-pgid, 0);
    process.kill(-pgid, "SIGKILL");
    await pollUntilGone(pgid, 5_000);
  } catch {
    // already gone
  }
}

export function registerAutopilotUnlockCommand(
  autopilot: Command,
  deps: RegisterAutopilotUnlockCommandDeps,
): void {
  autopilot
    .command("unlock")
    .description("Gỡ lock mồ côi sau crash/SIGKILL/reboot (không gỡ khi supervisor còn sống)")
    .option("--mission <id>", "Mission ID")
    .option("--repo <path>", "Repo path để resolve mission")
    .option("--json", "Output JSON")
    .action(async (opts: { mission?: string; repo?: string; json?: boolean }) => {
      const workspaceRoot = await deps.resolveWorkspaceRoot(opts.repo);
      const resolved = await resolveMission({ workspaceRoot, explicitMissionId: opts.mission });

      const result = await releaseOrphanedSupervisorLocks({
        machineLockPath: deps.machineLockPath,
        controlRoot: resolved.controlRoot,
        missionId: resolved.missionId,
        // BUG-2 fix: lets a successful release also reclaim the stale
        // attempt binding, so the next `run` doesn't sit "blocked" silently
        // for up to ~61 min waiting on expires_at.
        missionWorkspace: join(resolved.missionHome, "workspace"),
        liveness: realLivenessProbe,
        killGroup: defaultKillGroup,
        processGroupId: defaultProcessGroupId,
      });

      if (result.killedWorkerPgid !== undefined) {
        await writeUnlockKilledWorkerMarker(resolved.controlRoot, {
          killed_worker_pgid: result.killedWorkerPgid,
          released_at: new Date().toISOString(),
        });
      }

      if (opts.json) {
        writeMachineReadableJson({
          command: "autopilot unlock",
          status: result.status,
          lock_path: deps.machineLockPath,
          removed: result.removed,
          ...(result.killedWorkerPgid !== undefined
            ? { killed_worker_pgid: result.killedWorkerPgid }
            : {}),
          ...(result.hint ? { hint: result.hint } : {}),
        });
      } else {
        log.info(`unlock: ${result.status}`);
        for (const p of result.removed) log.info(`  đã gỡ: ${p}`);
        if (result.killedWorkerPgid !== undefined) {
          log.info(`  đã kill worker group pgid ${result.killedWorkerPgid}`);
        }
        if (result.hint) log.error(result.hint);
      }

      if (result.status.startsWith("refused_")) process.exit(1);
    });
}
